import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { motion } from 'framer-motion';
import * as Lucide from 'lucide-react';
import { sendMagicLink } from '@mental-mint/shared-utils';
import { ButtonStylized, ButtonStylizedType } from '../../ButtonStylized/ButtonStylized';
import { ButtonStylizedFillable, FillDirection } from '../../ButtonStylizedFillable/ButtonStylizedFillable';
import { BasePageProps } from '../../Page/PageTypes';
import { RootState } from '../../Store/store';
import './PageLogin.css';

export interface PageLoginProps extends BasePageProps {
  /** Game ID for magic link generation */
  gameId: string;
  
  /** Custom prompt banner message */
  message?: string;
  subMessage?: string;
}

/**
 * PageLogin - Full-page email-based magic link authentication
 * 
 * Can be rendered as a modal or full page. Games can navigate to this
 * page to prompt user login without needing to manage the login logic.
 */
export const PageLogin: React.FC<PageLoginProps> = ({ 
  onRequestClose, 
  gameId,
  message = "Save your progress across devices with a free account!",
  subMessage = "If you already have an account, enter your email to receive a login link.",
}) => {
  const { currentGame } = useSelector((state: RootState) => state.gameSession);
  const primaryColor = currentGame?.colorPrimary || '#6B8E23';
  const secondaryColor = currentGame?.colorSecondary || '#8B4513';
  
  // Shared button styles for consistency
  const primaryButtonStyles = { flex: 1, height: '3rem', fontSize: '1.4rem' };
  
  const [email, setEmail] = useState('');
  const [consentToReminders, setConsentToReminders] = useState(true); // Default to true for marketing emails
  const [isLoading, setIsLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [error, setError] = useState('');
  const [countdown, setCountdown] = useState(10);

  const isValidEmail = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!email || !isValidEmail(email)) {
      setError('Please enter a valid email address');
      return;
    }

    setIsLoading(true);

    try {
      // Pass current URL as returnUrl to redirect back here after email verification
      // This ensures localhost and preview builds work correctly
      const returnUrl = window.location.origin;
      await sendMagicLink(email, gameId, returnUrl, consentToReminders);
      setEmailSent(true);
    } catch (err: any) {
      // ApiClient provides user-friendly error messages
      setError(err.message || 'Something went wrong. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setEmail('');
    setConsentToReminders(false);
    setEmailSent(false);
    setError('');
    setCountdown(10);
    if (onRequestClose) {
      onRequestClose();
    }
  };

  const handleRetry = async () => {
    setEmailSent(false);
    setCountdown(10);
    setError('');
  };

  // Countdown timer effect when email is sent
  React.useEffect(() => {
    if (emailSent && countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [emailSent, countdown]);

  if (emailSent) {
    const isRetryEnabled = countdown === 0;

    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="page-login-content page-login-success"
      >
        <Lucide.CheckCircle size={64} className="page-login-success-icon" />
        <p className="page-login-text">
          We've sent a login link to <strong>{email}</strong>
        </p>
        <p className="page-login-subtext">
          Click the link in your email to sign in. The link will expire in 15 minutes.
        </p>
        <p className="page-login-tip">
          💡 Don't see it? Check your spam or junk folder.
        </p>
        
        <div style={{ marginTop: '1rem', width: '100%' }}>
          <ButtonStylizedFillable
            buttonType={isRetryEnabled ? ButtonStylizedType.Primary : ButtonStylizedType.Disabled}
            fillDirection={FillDirection.LEFT_TO_RIGHT}
            fillDurationMs={10000}
            backgroundColor={isRetryEnabled ? primaryColor : '#e0e0e0'}
            fillColor={isRetryEnabled ? primaryColor : '#798678'}
            showGlowEffect={false}
            text={`Resend link${!isRetryEnabled ? ` (${countdown}s)` : ''}`}
            onClick={handleRetry}
            disabled={!isRetryEnabled}
            onFillComplete={() => {}}
            additionalStyles={{ 
              ...primaryButtonStyles,
              width: '100%',
            }}
          />
        </div>

        <button 
          type="button" 
          className="page-login-skip" 
          onClick={handleClose}
          style={{ marginTop: '1rem' }}
        >
          Skip for now
        </button>
      </motion.div>
    );
  }

  return (
    <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="page-login-content"
    >
        {/* Icon Container */}
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
          style={{
            width: '100px',
            height: '100px',
            borderRadius: '50%',
            background: `${primaryColor}20`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '10px auto 0px'
          }}
        >
          <Lucide.User 
            size={65} 
            color={primaryColor}
            strokeWidth={2}
          />
        </motion.div>

        <div 
            className="page-login-message"
            style={{
                color: primaryColor,
            }}
        >
            <div>
                <p>{message}</p>
                <div className="page-login-submessage" style={{color: secondaryColor}}>
                    <p>{subMessage}</p>
                </div>
            </div>
        </div>
      
      <form onSubmit={handleSubmit} className="page-login-form">
            <input
              type="email"
              placeholder="your.email@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isLoading}
              className={`page-login-input ${error ? 'page-login-input-error' : ''}`}
            />

            {error && (
              <p className="page-login-error">{error}</p>
            )}

            <label 
              className="page-login-checkbox-label"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                marginTop: '1rem',
                cursor: 'pointer',
                userSelect: 'none',
              }}
            >
              <input
                type="checkbox"
                checked={consentToReminders}
                onChange={(e) => setConsentToReminders(e.target.checked)}
                disabled={isLoading}
                style={{
                  width: '18px',
                  height: '18px',
                  cursor: 'pointer',
                  accentColor: primaryColor,
                }}
              />
              <span style={{ fontSize: '0.9rem', color: secondaryColor }}>
                Send me game reminders and updates
              </span>
            </label>

            <div className="page-login-actions">
              <ButtonStylized
                buttonType={ButtonStylizedType.Primary}
                text={isLoading ? 'Sending...' : 'Send Login Link'}
                buttonHtmlType="submit"
                icon={isLoading ? <Lucide.Loader2 size={18} className="spinner" /> : undefined}
                disabled={isLoading}
                additionalStyles={primaryButtonStyles}
              />
            </div>            
            <button 
              type="button" 
              className="page-login-skip" 
              onClick={handleClose}
              disabled={isLoading}
            >
              Skip for now
            </button>
          </form>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .spinner {
          animation: spin 1s linear infinite;
        }
      `}</style>
    </motion.div>
  );
};
